import { cn } from '@doccareplus/shared/lib/utils';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  /** If true, centers the loader in a full-viewport screen overlay */
  fullScreen?: boolean;
  className?: string;
}

const SIZE_MAP = {
  sm: 'w-6 h-6',
  md: 'w-9 h-9',
  lg: 'w-14 h-14',
};

/**
 * LoadingSpinner — Custom detailed heartbeat vitality spinner.
 * Features a dual-layered outer ring (spinning track) with an active inner heartbeat wave (no center dot).
 * The ring only ever shows a short arc (not a near-complete circle) so the motion reads clearly as spinning.
 *
 * NO business logic — pure presentational.
 */
export function LoadingSpinner({ size = 'md', fullScreen = false, className }: LoadingSpinnerProps) {
  const spinnerElement = (
    <svg
      className={cn('text-[var(--primary)]', SIZE_MAP[size], className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 40 40"
      aria-label="Loading"
    >
      {/* Background track ring */}
      <circle cx="20" cy="20" r="17" stroke="var(--primary-weak)" strokeWidth="2.5" />
      
      {/* Animated Segmented Outer Ring */}
      <circle
        className="spinner-vitality-ring"
        cx="20"
        cy="20"
        r="17"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      
      {/* Detailed Heartbeat Wave (Inside) */}
      <path
        className="spinner-heartbeat-path"
        d="M11 20 H16 L18 14 L21 26 L23 18 L25 21 H29"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--bg)]">
        {spinnerElement}
      </div>
    );
  }

  return spinnerElement;
}

